// src/pages/admin/dashboard/index.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import adminDashboardService from '../../../services/admin/admin-dashboardService';
import AdminLayout from '../layout';
import {
  PaymentsIcon,
  OrdersIcon,
  CustomersIcon,
  GiftIcon,
  ArrowUp,
  ArrowDown,
  SearchIcon,
  ArrowLeft,
  ArrowRight
} from '../../../public/icons';

const MONTHS_MAP = {
  'Last 3 Months': 3,
  'Last 6 Months': 6,
  'Last 8 Months': 8,
  'Last 12 Months': 12
};

const STATUS_OPTIONS = [
  'All status',
  'Delivered',
  'Content review',
  'In production',
  'Shipped',
  'Pending'
];

/* ───────── defensive helpers ───────── */

const safeStat = (entry, fallbackFormatted = '0') => {
  if (!entry || typeof entry !== 'object') {
    return { formatted: fallbackFormatted, change: 0 };
  }
  return {
    formatted:
      entry.formatted ??
      (typeof entry.value === 'number'
        ? entry.value.toLocaleString()
        : fallbackFormatted),
    change: typeof entry.change === 'number' ? entry.change : 0
  };
};

const emptyPagination = {
  page: 1,
  limit: 10,
  total: 0,
  pages: 1,
  from: 0,
  to: 0
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState('Last 8 Months');
  const [statusFilter, setStatusFilter] = useState('All status');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  /* ───────── data fetching ───────── */

  const fetchDashboardData = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          months: MONTHS_MAP[selectedPeriod] || 8,
          status: statusFilter,
          search: searchQuery || undefined,
          page: currentPage,
          limit: 10,
          ...overrides
        };

        const response = await adminDashboardService.getDashboardData(params);
        console.log('Dashboard data', response.data);

        const payload = response?.data ?? response ?? null;

        if (response?.success === false) {
          setError(response?.message || 'Failed to load dashboard');
          setDashboardData(null);
        } else if (payload && typeof payload === 'object') {
          setDashboardData(payload);
        } else {
          setDashboardData(null);
        }
      } catch (err) {
        console.error('Error fetching dashboard:', err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load dashboard'
        );
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedPeriod, statusFilter, searchQuery, currentPage]
  );

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, statusFilter, currentPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchDashboardData({ page: 1 });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  /* ───────── derived data ───────── */

  const stats = useMemo(() => {
    const s = dashboardData?.stats || {};

    const totalRevenue = safeStat(s.totalRevenue, '₦0');
    const activeOrders = safeStat(s.activeOrders, '0');
    const activeCustomers = safeStat(s.activeCustomers, '0');
    const futureGiftsPending = safeStat(s.futureGiftsPending, '0');

    return [
      {
        title: 'Total Revenue',
        value: totalRevenue.formatted,
        change: adminDashboardService.formatChange(totalRevenue.change),
        changeType: adminDashboardService.getChangeType(totalRevenue.change),
        icon: <PaymentsIcon size={20} color="#2F8F5B" />,
        bgColor: 'bg-[#E7F4ED]'
      },
      {
        title: 'Active Orders',
        value: activeOrders.formatted,
        change: adminDashboardService.formatChange(activeOrders.change),
        changeType: adminDashboardService.getChangeType(activeOrders.change),
        icon: <OrdersIcon size={20} color="#412F8F" />,
        bgColor: 'bg-[#E8E7F4]'
      },
      {
        title: 'Active Customer',
        value: activeCustomers.formatted,
        change: adminDashboardService.formatChange(activeCustomers.change),
        changeType: adminDashboardService.getChangeType(activeCustomers.change),
        icon: <CustomersIcon size={20} color="#8F342F" />,
        bgColor: 'bg-[#F4E7E7]'
      },
      {
        title: 'Future Gifts Pending',
        value: futureGiftsPending.formatted,
        change: adminDashboardService.formatChange(futureGiftsPending.change),
        changeType: adminDashboardService.getChangeType(
          futureGiftsPending.change
        ),
        icon: <GiftIcon size={20} color="#8F2F8A" />,
        bgColor: 'bg-[#F4E7F2]'
      }
    ];
  }, [dashboardData]);

  const revenueChartData = useMemo(
    () => dashboardData?.revenueSnapshot?.series || [],
    [dashboardData]
  );

  const currentMonthSummary =
    dashboardData?.revenueSnapshot?.currentMonth?.summary || '';
  const currentMonthTotal =
    dashboardData?.revenueSnapshot?.currentMonth?.incomeFormatted || '₦0';

  const chartMax = useMemo(() => {
    if (!revenueChartData.length) return 1000;
    const max = Math.max(
      1,
      ...revenueChartData.map((r) =>
        Math.max(r?.income || 0, r?.expense || 0)
      )
    );
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude;
  }, [revenueChartData]);

  /* ── Build SVG polyline point strings for income + expense ──
     Each point sits at the vertical center of its bar pair.
     viewBox is 0..100 wide, 0..100 tall — CSS scales the SVG to fit. */
  const chartLines = useMemo(() => {
    const n = revenueChartData.length;
    if (n === 0) {
      return { incomePoints: '', expensePoints: '', points: [] };
    }

    // Each bar group occupies 1/n of the width. Center of group i = (i + 0.5)/n
    const points = revenueChartData.map((item, i) => {
      const x = ((i + 0.5) / n) * 100;
      const incomeVal = item?.income || 0;
      const expenseVal = item?.expense || 0;

      // SVG y is inverted: 0 at top, 100 at bottom
      const incomeY = 100 - Math.min((incomeVal / chartMax) * 100, 100);
      const expenseY = 100 - Math.min((expenseVal / chartMax) * 100, 100);

      return {
        x,
        incomeY,
        expenseY,
        income: incomeVal,
        expense: expenseVal,
        month: item?.month || ''
      };
    });

    const incomePoints = points.map((p) => `${p.x},${p.incomeY}`).join(' ');
    const expensePoints = points.map((p) => `${p.x},${p.expenseY}`).join(' ');

    return { incomePoints, expensePoints, points };
  }, [revenueChartData, chartMax]);

  const recentActivity = useMemo(
    () => dashboardData?.recentActivity || [],
    [dashboardData]
  );

  const recentOrders = useMemo(
    () => dashboardData?.recentOrders || [],
    [dashboardData]
  );

  const pagination = dashboardData?.pagination || emptyPagination;

  /* ───────── pagination buttons ───────── */

  const pageButtons = useMemo(() => {
    const total = pagination.pages || 1;
    const current = pagination.page || 1;
    const pages = [];
    const windowSize = 3;

    let start = Math.max(1, current - Math.floor(windowSize / 2));
    let end = Math.min(total, start + windowSize - 1);
    if (end - start + 1 < windowSize) {
      start = Math.max(1, end - windowSize + 1);
    }

    for (let p = start; p <= end; p++) pages.push(p);

    return {
      list: pages,
      showTrailingEllipsis: end < total,
      lastPage: total
    };
  }, [pagination]);

  /* ───────── render ───────── */

  const showInitialSpinner = loading && !dashboardData;

  if (showInitialSpinner) {
    return (
      <AdminLayout onLogout={handleLogout}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-4 text-gray-600 text-sm sm:text-base">
              Loading dashboard data...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="space-y-4 sm:space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded">
            {error}
            <button
              onClick={() => fetchDashboardData()}
              className="ml-3 underline font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-sm p-3 sm:p-4 md:p-5 w-full"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-md ${stat.bgColor}`}>
                  {stat.icon}
                </div>
                <div
                  className={`flex items-center gap-1 text-xs sm:text-sm font-normal ${
                    stat.changeType === 'increase'
                      ? 'text-[#2F8F5B]'
                      : 'text-[#8F342F]'
                  }`}
                >
                  {stat.changeType === 'increase' ? (
                    <ArrowUp size={12} />
                  ) : (
                    <ArrowDown size={12} />
                  )}
                  {stat.change}
                </div>
              </div>
              <div className="text-xl sm:text-2xl md:text-3xl text-left font-medium text-[#0E1628] mb-1 sm:mb-2.5">
                {stat.value}
              </div>
              <h3 className="text-xs sm:text-sm text-left font-normal text-[#6B635D]">
                {stat.title}
              </h3>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {/* Revenue Snapshot */}
          <div className="lg:col-span-2 bg-white rounded-sm p-3 sm:p-4 md:p-5">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
              <h2 className="text-sm font-normal text-[#000000]/50">
                Revenue Snapshot
              </h2>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-blue-500" />
                  <span className="text-xs sm:text-sm text-gray-600">
                    Income
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-500" />
                  <span className="text-xs sm:text-sm text-gray-600">
                    Expense
                  </span>
                </div>

                {/* Period selector */}
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="hidden sm:block p-1.5 pl-2 pr-6 border border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574]"
                >
                  {Object.keys(MONTHS_MAP).map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4 sm:mb-6 text-left">
              <div className="text-xl sm:text-2xl font-medium text-secondary mb-1">
                {currentMonthTotal}
              </div>
              <div className="text-xs sm:text-sm text-[#6B635D]">
                {currentMonthSummary || '—'}
              </div>
            </div>

            {/* Chart — bars + line overlays */}
            <div className="h-48 sm:h-56 md:h-64 relative overflow-x-auto">
              <div className="min-w-[500px] sm:min-w-full relative h-full">
                {/* Y-axis */}
                <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[10px] sm:text-xs text-[#6B635D] py-2 sm:py-4">
                  <span>{(chartMax / 1000).toFixed(0)}K</span>
                  <span>{((chartMax * 0.8) / 1000).toFixed(0)}K</span>
                  <span>{((chartMax * 0.6) / 1000).toFixed(0)}K</span>
                  <span>{((chartMax * 0.4) / 1000).toFixed(0)}K</span>
                  <span>{((chartMax * 0.2) / 1000).toFixed(0)}K</span>
                  <span>0</span>
                </div>

                {/* Plot area (bars + lines) */}
                <div className="ml-6 sm:ml-8 h-full relative">
                  {/* Horizontal gridlines */}
                  <div className="absolute inset-0 flex flex-col justify-between py-2 sm:py-4 pointer-events-none">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="border-t border-dashed border-[#E5E5E5] w-full"
                      />
                    ))}
                  </div>

                  {revenueChartData.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center text-xs text-[#6B635D]">
                      No revenue data for this period
                    </div>
                  ) : (
                    <>
                      {/* Bars */}
                      <div className="relative h-full flex items-end justify-between">
                        {revenueChartData.map((item, index) => (
                          <div
                            key={index}
                            className="flex flex-col items-center"
                          >
                            <div className="flex items-end space-x-0.5 sm:space-x-1 mb-1 sm:mb-2">
                              <div
                                className="w-3 sm:w-4 bg-blue-500/40 rounded-t"
                                style={{
                                  height: `${Math.min(
                                    ((item?.income || 0) / chartMax) * 100,
                                    100
                                  )}%`,
                                  minHeight: '4px'
                                }}
                                title={`Income: ₦${(
                                  item?.income || 0
                                ).toLocaleString()}`}
                              />
                              <div
                                className="w-3 sm:w-4 bg-orange-500/40 rounded-t"
                                style={{
                                  height: `${Math.min(
                                    ((item?.expense || 0) / chartMax) * 100,
                                    100
                                  )}%`,
                                  minHeight: '4px'
                                }}
                                title={`Expense: ₦${(
                                  item?.expense || 0
                                ).toLocaleString()}`}
                              />
                            </div>
                            <span className="text-[10px] sm:text-xs text-[#6B635D]">
                              {item?.month || ''}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Line overlay (SVG covering the plot area, above bars) */}
                      <svg
                        className="absolute left-0 right-0 top-0 pointer-events-none"
                        style={{
                          height: 'calc(100% - 24px)', // leave room for month labels
                          width: '100%'
                        }}
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        {/* Income line */}
                        <polyline
                          points={chartLines.incomePoints}
                          fill="none"
                          stroke="#3B82F6"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                        />
                        {/* Expense line */}
                        <polyline
                          points={chartLines.expensePoints}
                          fill="none"
                          stroke="#F97316"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                        />
                        {/* Dots on income line */}
                        {chartLines.points.map((p, i) => (
                          <circle
                            key={`inc-${i}`}
                            cx={p.x}
                            cy={p.incomeY}
                            r="1.2"
                            fill="#3B82F6"
                            stroke="#fff"
                            strokeWidth="0.5"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                        {/* Dots on expense line */}
                        {chartLines.points.map((p, i) => (
                          <circle
                            key={`exp-${i}`}
                            cx={p.x}
                            cy={p.expenseY}
                            r="1.2"
                            fill="#F97316"
                            stroke="#fff"
                            strokeWidth="0.5"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </svg>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-sm p-3 sm:p-4 md:p-5">
            <div className="flex justify-between items-center mb-4 sm:mb-5 md:mb-7">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                Recent Activity
              </h2>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {recentActivity.length === 0 ? (
                <div className="text-xs sm:text-sm text-[#6B635D] text-center py-6">
                  No recent activity
                </div>
              ) : (
                recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      activity?.link && navigate(activity.link)
                    }
                    className={`flex items-start gap-2 sm:gap-3 p-2 rounded transition-colors duration-150 ${
                      activity?.link ? 'cursor-pointer hover:bg-gray-50' : ''
                    }`}
                  >
                    <div
                      className={`p-1.5 sm:p-2 rounded-lg ${
                        activity?.iconColor || 'bg-[#6B635D]'
                      }`}
                    >
                      <div className="w-2 h-2 bg-white/70 rounded-full" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-normal text-xs sm:text-sm text-[#0E1628]">
                          {activity?.user || '—'}
                        </span>
                      </div>
                      <div className="text-[10px] sm:text-xs font-normal text-[#6B635D] mt-0.5 sm:mt-1">
                        {activity?.time || ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-sm overflow-hidden">
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                Recent Orders
              </h2>

              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {/* Search Box */}
                <div className="relative flex-1 sm:flex-initial">
                  <div className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={16} color="#6B635D" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-1.5 sm:p-2 pl-7 sm:pl-10 pr-2 border w-full sm:w-40 md:w-52 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs sm:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent text-sm"
                  />
                </div>

                {/* Status Filter Dropdown */}
                <div className="relative flex-1 sm:flex-initial">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="p-1.5 sm:p-2 pl-2 sm:pl-3 pr-6 sm:pr-8 border w-full sm:w-36 md:w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs sm:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-1.5 sm:right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ArrowDown size={12} color="#6B635D" />
                  </div>
                </div>

                {/* See All Orders Button */}
                <button
                  onClick={() => navigate('/admin/orders')}
                  className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 border border-[#DBDBDB] text-xs sm:text-sm font-normal text-[#1C2A46] hover:bg-gray-50 hover:border-[#1C2A46] transition-colors whitespace-nowrap"
                >
                  See All Orders
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[600px] md:min-w-full divide-y divide-[#DBDBDB]">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  {['Order Id', 'Date', 'Customer', 'Gift Type', 'Status'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-xs sm:text-sm text-[#6B635D] py-8"
                    >
                      No orders found
                    </td>
                  </tr>
                ) : (
                  recentOrders.map((order) => (
                    <tr
                      key={order?.id || order?.orderId}
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() =>
                        order?.id && navigate(`/admin/orders/${order.id}`)
                      }
                    >
                      <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {order?.orderId || '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-normal text-[#0E1628]">
                          {adminDashboardService.formatDate(order?.date)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {order?.customer || '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {order?.giftType || '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span
                            className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs leading-5 font-semibold rounded-full ${
                              order?.bgColor || 'bg-gray-100'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${
                                order?.dotColor || 'bg-gray-500'
                              }`}
                            />
                            <span
                              className={order?.statusColor || 'text-gray-700'}
                            >
                              {order?.status || 'Unknown'}
                            </span>
                            <ArrowDown
                              size={10}
                              className={order?.statusColor || 'text-gray-700'}
                            />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="text-[10px] sm:text-sm text-[#6B635D] order-2 sm:order-1">
                Showing{' '}
                <span className="font-medium">
                  {pagination.from}–{pagination.to}
                </span>{' '}
                out of{' '}
                <span className="font-medium">{pagination.total}</span>
              </div>

              <div className="flex justify-center items-center gap-2 sm:gap-4 order-1 sm:order-2 overflow-x-auto pb-1 w-full sm:w-auto">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 sm:p-2 border border-[#DBDBDB] hover:border-[#1C2A46] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 text-[#1C2A46]" />
                </button>

                <div className="flex gap-1 sm:gap-2">
                  {pageButtons.list.map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`py-1 sm:py-2 px-2 sm:px-3 md:px-4 min-w-[28px] sm:min-w-[35px] md:min-w-[40px] text-xs sm:text-sm ${
                        page === pagination.page
                          ? 'bg-[#55000C] text-white'
                          : 'text-[#1C2A46] hover:bg-white border border-[#DBDBDB]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {pageButtons.showTrailingEllipsis && (
                    <>
                      <span className="py-1 sm:py-2 px-1 sm:px-2 text-[10px] sm:text-sm text-[#6B635D]">
                        ...
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage(pageButtons.lastPage)
                        }
                        className={`py-1 sm:py-2 px-2 sm:px-3 md:px-4 text-[#1C2A46] hover:bg-white border border-[#DBDBDB] text-xs sm:text-sm ${
                          pageButtons.lastPage === pagination.page
                            ? 'bg-[#55000C] text-white'
                            : ''
                        }`}
                      >
                        {pageButtons.lastPage}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(pagination.pages, p + 1)
                    )
                  }
                  disabled={pagination.page >= pagination.pages}
                  className="p-1.5 sm:p-2 border border-[#DBDBDB] hover:border-[#1C2A46] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-[#1C2A46]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;